// Imports
import * as Sentry from '@sentry/browser';


// The script tag that carries prerendered data from the snapshot to the client
const scriptID = '__PRERENDER_DATA__';


// Data recorded during the current prerender and mirrored into the script tag
const recorded: Record<string, unknown> = {};


// Data parsed from the snapshot on the client
let parsed: Record<string, unknown> | null = null;


// Parse the data the prerender left in the page
const parseSnapshot = (): Record<string, unknown> => {
	try {
		return JSON.parse(document.getElementById(scriptID)?.textContent ?? '{}') as Record<string, unknown>;
	} catch (error) {
		// Report the corruption, since we wrote this payload ourselves and every visitor is served it
		Sentry.captureException(error);
		
		return {};
	}
};


// Prerendered data, carried from the prerender to the client so content doesn't flash back to a loading skeleton
const prerenderData = {
	/** Record data into the prerendered snapshot */
	record(key: string, value: unknown): void {
		// Skip if not prerendering
		if (!window.IS_PRERENDERING) {
			return;
		}
		
		
		// Update recorded data
		recorded[key] = value;
		
		
		// Get or create the script tag
		let script = document.getElementById(scriptID);
		
		if (!script) {
			script = document.createElement('script');
			script.id = scriptID;
			script.setAttribute('type', 'application/json');
			document.head.appendChild(script);
		}
		
		
		// Write recorded data
		//  -> Escape `<` so recorded content can't close this tag early and break the captured HTML
		script.textContent = JSON.stringify(recorded).replace(/</g, '\\u003c');
	},
	
	
	/** Read data recorded into the snapshot during prerendering */
	read<T>(key: string): T | undefined {
		if (!parsed) {
			parsed = parseSnapshot();
		}
		
		return parsed[key] as T | undefined;
	},
	
	
	/** Restore data from the snapshot rather than refetching it; returns whether it was restored */
	restore<T>(key: string, apply: (data: T) => void): boolean {
		const data = prerenderData.read<T>(key);
		
		if (!data || window.IS_PRERENDERING) {
			return false;
		}
		
		apply(data);
		
		return true;
	},
	
	
	/** Restore data from the snapshot then revalidate by refetching in the background; returns a function that discards the refetch's result */
	revalidate<T>(key: string, apply: (data: T) => void, fetchFresh: () => Promise<T>, onError?: () => void): () => void {
		const wasRestored = prerenderData.restore<T>(key, apply);
		let isDiscarded = false;
		
		fetchFresh()
			.then((data) => {
				prerenderData.record(key, data);
				
				if (!isDiscarded) {
					apply(data);
				}
			})
			.catch(() => {
				if (!wasRestored && !isDiscarded) {
					onError?.();
				}
			});
		
		return () => {
			isDiscarded = true;
		};
	},
};

export default prerenderData;
